import { useCallback, useState } from "react";
import { useAuthenticatedFetch } from "./useAuthenticatedFetch";
import { showToast } from "../utils/toast";
import { DRIVE_UPLOAD_ENDPOINT } from "../../constant/api-endpoints";
import { FileWithUpload } from "../types/FileWithUpload";

const MAX_FILES = 5;

export const useFileUpload = () => {
  const [uploadedFiles, setUploadedFiles] = useState<FileWithUpload[]>([]);
  const authenticatedFetch = useAuthenticatedFetch();

  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await authenticatedFetch(DRIVE_UPLOAD_ENDPOINT, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload file: ${response.status}`);
    }

    const data = await response.json();
    return data.id;
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (selectedFiles.length + uploadedFiles.length > MAX_FILES) {
      showToast(`Vous ne pouvez télécharger que ${MAX_FILES} fichiers maximum`, "error");
      return;
    }

    if (selectedFiles.length > 0) {
      // Add files with initial uploading state
      const newFiles: FileWithUpload[] = selectedFiles.map((file) => ({
        file,
        fileId: null,
        isUploading: true,
        uploadError: null,
      }));

      setUploadedFiles((prev) => [...prev, ...newFiles]);

      // Upload all new files in parallel
      await Promise.all(
        newFiles.map(async (fileWithUpload) => {
          try {
            const fileId = await uploadFile(fileWithUpload.file);
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.file === fileWithUpload.file
                  ? {
                      ...f,
                      fileId,
                      isUploading: false,
                      uploadError: null,
                    }
                  : f
              )
            );
          } catch (err) {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.file === fileWithUpload.file
                  ? {
                      ...f,
                      isUploading: false,
                      uploadError: err instanceof Error ? err.message : "Upload failed",
                    }
                  : f
              )
            );
          }
        })
      );
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setUploadedFiles((prev) => prev.filter((f) => f.file !== fileToRemove));
  };

  const clearUploadedFiles = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  const hasUploadingFiles = uploadedFiles.some((f) => f.isUploading);

  const getFileIds = useCallback(() => {
    return uploadedFiles
      .filter((f) => f.fileId && !f.uploadError)
      .map((f) => f.fileId as string);
  }, [uploadedFiles]);

  return {
    uploadedFiles,
    handleFileChange,
    handleRemoveFile,
    clearUploadedFiles,
    hasUploadingFiles,
    getFileIds,
  };
};
